import React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cn } from '@/lib/utils';

interface TouchOptimizedButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  children: React.ReactNode;
  variant?: 'primary' | 'secondary' | 'outline' | 'ghost';
  size?: 'sm' | 'md' | 'lg' | 'touch';
  className?: string;
  loading?: boolean;
  touchFeedback?: boolean;
  asChild?: boolean;
}

const TouchOptimizedButton: React.FC<TouchOptimizedButtonProps> = ({
  children,
  variant = 'primary',
  size = 'md',
  className,
  loading = false,
  touchFeedback = true,
  disabled,
  asChild = false,
  ...props
}) => {
  const Comp = asChild ? Slot : 'button';
  const baseClasses = cn(
    // Base styles
    'inline-flex items-center justify-center font-medium transition-all duration-200',
    'focus:outline-none focus:ring-2 focus:ring-offset-2',
    'disabled:opacity-50 disabled:cursor-not-allowed',
    
    // Touch optimizations
    touchFeedback && 'active:scale-95 select-none',
    'touch-manipulation', // Prevents double-tap zoom
    
    // Minimum touch target size (44px)
    size === 'touch' && 'min-h-[44px] min-w-[44px] px-6 py-3',
    
    className
  );

  const variants = {
    primary: 'bg-primary text-primary-foreground hover:bg-primary/90 focus:ring-primary',
    secondary: 'bg-secondary text-secondary-foreground hover:bg-secondary/80 focus:ring-secondary',
    outline: 'border border-input bg-background hover:bg-accent hover:text-accent-foreground focus:ring-primary',
    ghost: 'hover:bg-accent hover:text-accent-foreground focus:ring-primary'
  };

  const sizes = {
    sm: 'h-9 px-3 text-sm rounded-md',
    md: 'h-10 px-4 py-2 rounded-md',
    lg: 'h-11 px-8 rounded-lg text-lg',
    touch: 'min-h-[44px] px-6 py-3 rounded-lg text-base' // WCAG compliant touch target
  };

  // When using asChild, we need to handle the case where loading might add extra children
  if (asChild) {
    // For asChild, we can't add loading states because Slot expects exactly one child
    return (
      <Slot
        className={cn(baseClasses, variants[variant], sizes[size])}
        {...props}
      >
        {children}
      </Slot>
    );
  }

  return (
    <button
      className={cn(baseClasses, variants[variant], sizes[size])}
      disabled={disabled || loading}
      {...props}
    >
      {loading && (
        <svg
          className="animate-spin -ml-1 mr-2 h-4 w-4"
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
        >
          <circle
            className="opacity-25"
            cx="12"
            cy="12"
            r="10"
            stroke="currentColor"
            strokeWidth="4"
          />
          <path
            className="opacity-75"
            fill="currentColor"
            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
          />
        </svg>
      )}
      {children}
    </button>
  );
};

export default TouchOptimizedButton;